import api from './api';
import { stripNestedMongoIds } from '../utils/apiPayload';

export interface DayTime {
  hour?: number;
  minute?: number;
}

export interface Subtournament {
  _id?: string;
  id?: string;
  mainTournament?: string;
  name?: string;
  description?: string | null;
  image?: string | null;
  images?: string[];
  dates?: number[];
  categories?: string[];
  type?: string;
  entryEnabled?: boolean;
  entryFee?: number | null;
  /** Mehrere Startgelder mit Bezeichnung (2026-09-26); entryFee = das erste (Standard). ADDITIV. */
  entryFees?: { label: string; amount: number }[] | null;
  entryLimit?: number | null;
  entryLink?: string | null;
  entryCode?: string | null;
  /** Anmelde-Passwort nötig (2026-10-06, additiv) */
  entryCodeRequired?: boolean | null;
  /** Anmelde-Passwort im Klartext – nur für Verwalter in der Antwort (2026-10-06) */
  entryCodePlain?: string | null;
  entryDeadline?: number | null;
  entryDeadlineOnSite?: DayTime | null;
  entryTime?: DayTime | null;
  start?: DayTime | null;
  waitingEnabled?: boolean;
  minAge?: number | null;
  maxAge?: number | null;
  teamSize?: number | null;
  teamSizeMax?: number | null;
  trophies?: boolean | null;
  gameMode?: string;
  cancelled?: boolean;
  createdAt?: number;
  updatedAt?: number;
  // Anmelde-Konfiguration (wie Backend subtournament-schema, alle nullable)
  customEntryFields?: any[] | null;
  customEntryHint?: string | null;
  entryChangeableUntil?: number | null;
  entryBirthdayRequired?: boolean | null;
  leaveEntryAllowed?: boolean | null;
  entryText?: string | null;
  // Erweiterte Anmelde-Felder (Backend additiv, alle nullable)
  registrationType?: string | null;
  requireConfirmation?: boolean | null;
  showEntryList?: boolean | null;
  showWaitingList?: boolean | null;
  entryEmailRequired?: boolean | null;
  entryPhoneRequired?: boolean | null;
  entryPlaceholder?: string | null;
}

export { buildSubtournamentCreatePayload } from '../utils/subtournamentPayload';
export type { SubtournamentCreateInput } from '../utils/subtournamentPayload';

class SubtournamentService {
  private cache: Subtournament[] | null = null;
  private byMain: Map<string, Subtournament[]> = new Map();

  async getAllSubtournaments(force = false): Promise<Subtournament[]> {
    if (this.cache && !force) return this.cache;
    try {
      const res = await api.get('/api/subtournaments');
      const data: Subtournament[] = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];
      this.cache = data;
      this.rebuildIndex();
      return data;
    } catch (err) {
      console.error('Fehler beim Laden der Subtournaments:', err);
      this.cache = [];
      this.byMain.clear();
      return [];
    }
  }

  private rebuildIndex() {
    this.byMain.clear();
    if (!this.cache) return;
    for (const sub of this.cache) {
      const main = sub.mainTournament;
      if (!main) continue;
      const arr = this.byMain.get(main) || [];
      arr.push(sub);
      this.byMain.set(main, arr);
    }
  }

  getByMainTournament(mainId?: string): Subtournament[] {
    if (!mainId) return [];
    return this.byMain.get(mainId) || [];
  }

  /**
   * Competitions EINES Turniers frisch vom Server (POST /api/tournament/subtournaments, public).
   * Marcel 27.09.2026: Die Detailseite zeigte neu angelegte Competitions nicht, weil sie aus
   * der einmal pro Sitzung geladenen Gesamtliste kamen (stale bis zum Neuladen). Fällt bei
   * Netzfehlern auf die Gesamtliste zurück, damit die Seite nie leer bleibt.
   */
  async getForTournament(mainId?: string): Promise<Subtournament[]> {
    if (!mainId) return [];
    try {
      const res = await api.post('/api/tournament/subtournaments', { id: mainId });
      const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : null;
      if (data) return data as Subtournament[];
    } catch {
      // Fallback unten
    }
    await this.getAllSubtournaments().catch(() => []);
    return this.getByMainTournament(mainId);
  }

  /** Einzelne Competition per Id laden (GET /api/subtournament/id/:id, public). */
  async getSubtournamentById(id: string): Promise<Subtournament | null> {
    if (!id) return null;
    try {
      const res = await api.get(`/api/subtournament/id/${encodeURIComponent(id)}`);
      const data = res.data;
      if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
      return data as Subtournament;
    } catch {
      return null;
    }
  }

  /**
   * Competition zu einem Hauptturnier anlegen (POST /api/subtournament/create).
   * Pflichtfelder: mainTournament (UUID), name, categories (min 1), dates (min 1).
   * Nur Turnier-Besitzer oder Admins (Backend prüft).
   */
  async createSubtournament(payload: Record<string, any>): Promise<Subtournament | null> {
    const res = await api.post('/api/subtournament/create', stripNestedMongoIds(payload));
    this.clear(); // Cache invalidieren, damit neue Competition überall auftaucht
    return (res.data as Subtournament) ?? null;
  }

  /** Competition aktualisieren (POST /api/subtournament/update, id required). */
  async updateSubtournament(payload: Record<string, any>): Promise<Subtournament | null> {
    const res = await api.post('/api/subtournament/update', stripNestedMongoIds(payload));
    this.clear();
    return (res.data as Subtournament) ?? null;
  }

  /** Competition löschen (POST /api/subtournament/delete). */
  async deleteSubtournament(id: string): Promise<void> {
    await api.post('/api/subtournament/delete', { id });
    this.clear();
  }

  clear() {
    this.cache = null;
    this.byMain.clear();
  }
}

const subtournamentService = new SubtournamentService();
export default subtournamentService;
